import React, { useContext } from 'react'
import { node, func, bool, string, array } from 'prop-types'
import { connect } from 'react-redux'
import { css } from '@emotion/react'
import { Link } from 'gatsby'

import { userLogOut } from '@/store/user'
import { isAuthorized, getUserName, isAppInstalled } from '@/store/selectors'
import mediaQueries from '@/layout/media-queries'
import { getMarketplaceSettingsLink } from '@/utils/products'
import useParamSelector from '@/hooks/useParamSelector'
import { ToggleContext } from '@/components/Toggle'
import { URLS } from '@/constants'
import { isHelpDesk } from '@/utils'

import ArrowLeft from '@/assets/icons/arrow-left.inline.svg'
import LCIcon from '@/assets/icons/logo-livechat-small.inline.svg'
import DeveloperConsoleIcon from '@/assets/icons/developer-console.inline.svg'
import ManageInstallationsIcon from '@/assets/icons/LC-install.inline.svg'
import LogoutIcon from '@/assets/icons/logout.inline.svg'

import PagesMenu from '../PagesMenu'
import NavLink from '../NavLink'
import SignUpButton from '../SignUpButton'
import LogInButton from '../LogInButton'

const mobileMenuWrapper = (theme) => css`
  position: fixed;
  top: 60px;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  border-top: 1px solid ${theme.colors.gray50};
  background-color: ${theme.colors.white};
  overflow-y: auto;
  z-index: 100;
`

const pagesItemsCss = css`
  flex-direction: column;
  justify-content: flex-start;
  padding: 10px 16px;

  ${mediaQueries.xsmallScreen(200)} {
    padding: 10px 48px;
  }

  ${mediaQueries.smallScreen()} {
    padding: 10px 60px;
  }

  > div {
    width: 100%;

    > a {
      margin: 10px 0;
    }
  }
`

export const userGreetingsCss = (theme) => css`
  font-family: ${theme.fontFamily};
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  margin: 10px 0;
`

const buttonsWrapperCss = css`
  margin-top: 20px;

  > a {
    margin-left: 20px;
  }
`

const subItemWrapperCss = (theme) => css`
  position: fixed;
  width: 100%;
  height: calc(100% - 60px);
  top: 60px;
  left: 0;
  z-index: 10000;
  background-color: ${theme.colors.white};

  > a {
    background-color: ${theme.colors.gray50};
    padding: 10px 16px;

    ${mediaQueries.xsmallScreen(200)} {
      padding: 10px 48px;
    }

    ${mediaQueries.smallScreen()} {
      padding: 10px 60px;
    }
  }

  > div {
    height: calc(100% - 60px);
    overflow-y: auto;
    padding: 0 16px;

    ${mediaQueries.xsmallScreen(200)} {
      padding: 10px 48px;
    }

    ${mediaQueries.smallScreen()} {
      padding: 10px 60px;
    }

    .nav-link {
      margin: 20px 0;
    }
  }
`

const secondLevelNavTitleCss = (theme) => css`
  background-color: ${theme.colors.gray50};
  margin: 0;

  &,
  &:hover,
  &:visited {
    color: ${theme.colors.blue600};
  }

  .nav-link__label {
    position: absolute;
    left: 0;
    transform: translateX(calc(50vw - 14px));
  }
`

const shopifyAppId = process.env.GATSBY_SHOPIFY_APP_ID || ''

const SubItemsWrapper = ({
  children,
  showDropdownOnClick,
  toggleDropdown,
  ...rest
}) => {
  return (
    <div css={subItemWrapperCss} {...rest}>
      <NavLink
        label="Apps"
        href="#"
        icon={
          <ArrowLeft
            css={(theme) => `
              fill: ${theme.colors.blue700};
              margin-top: 1px;
            `}
          />
        }
        onClick={toggleDropdown}
        css={secondLevelNavTitleCss}
      />
      <div>{children}</div>
    </div>
  )
}

const MobileMenu = ({
  userName,
  isUserAuthorized,
  logOut,
  authorizedUserItems,
}) => {
  const { isOpen, toggle } = useContext(ToggleContext)
  const isShopifyInstalled = useParamSelector(isAppInstalled, shopifyAppId)

  const onActiveLinkClick = () => {
    if (isOpen) {
      toggle()
    }
  }

  const addOnActiveLinkProps = (list) =>
    list.map((item) => {
      item.onActiveLinkClick = onActiveLinkClick

      if (item.subItems && !!item.subItems.length) {
        item.subItems = addOnActiveLinkProps(item.subItems)
      }

      return item
    })

  let pagesItems = [
    {
      to: URLS.APPS.LINK,
      label: 'Apps',
      tag: Link,
    },
    {
      label: URLS.EXPERTS.LABEL,
      to: URLS.EXPERTS.LINK,
      tag: Link,
    },
    {
      label: URLS.DEVELOPERS.LABEL,
      to: URLS.DEVELOPERS.LINK,
      tag: Link,
    },
    {
      type: 'divider',
      label: 'first-break',
    },
  ]

  authorizedUserItems = authorizedUserItems || [
    {
      type: 'custom',
      label: 'Username',
      customItem: () =>
        isUserAuthorized && <p css={userGreetingsCss}>Hi, {userName}</p>,
    },
    {
      href: process.env.GATSBY_IFRAME_URL,
      label: 'Open LiveChat App',
      icon: (
        <div>
          <LCIcon css={(theme) => `fill: ${theme.colors.cornflowerBlue};`} />
        </div>
      ),
    },
    {
      href: process.env.GATSBY_CONSOLE_URL,
      label: 'Developer Console',
      target: '_blank',
      icon: (
        <DeveloperConsoleIcon
          css={(theme) => `fill: ${theme.colors.gray900};`}
        />
      ),
    },
    {
      href: getMarketplaceSettingsLink(),
      label: 'My Installed Apps',
      icon: (
        <ManageInstallationsIcon
          css={(theme) => `fill: ${theme.colors.gray900};`}
        />
      ),
    },
    {
      type: 'divider',
      label: 'second-divider',
    },
    {
      href: '',
      icon: <LogoutIcon css={(theme) => `fill: ${theme.colors.error};`} />,
      label: 'Log out',
      onClick: (e) => {
        e.preventDefault()
        logOut()
      },
    },
  ]

  const unauthorizedUserItems = [
    {
      type: 'custom',
      label: 'login',
      customItem: () => (
        <div css={buttonsWrapperCss}>
          <LogInButton />
          <SignUpButton />
        </div>
      ),
    },
  ]

  pagesItems = addOnActiveLinkProps(pagesItems)

  if (isHelpDesk) {
    pagesItems = pagesItems
      .filter(({ label }) => label !== URLS.EXPERTS.LABEL)
      .filter(({ label }) =>
        isUserAuthorized && isShopifyInstalled
          ? label !== URLS.DEVELOPERS.LABEL
          : true
      )
  }

  if (isUserAuthorized) {
    pagesItems = [...pagesItems, ...authorizedUserItems]
  } else {
    pagesItems = [...pagesItems, ...unauthorizedUserItems]
  }

  return (
    <div css={mobileMenuWrapper}>
      <PagesMenu items={pagesItems} css={pagesItemsCss} />
    </div>
  )
}

const mapStateToProps = (state) => ({
  userName: getUserName(state),
  isUserAuthorized: isAuthorized(state),
})

const mapDispatchToProps = {
  logOut: userLogOut.pending,
}

SubItemsWrapper.propTypes = {
  children: node,
  toggleDropdown: func,
  showDropdownOnClick: bool,
}

MobileMenu.propTypes = {
  userName: string,
  isUserAuthorized: bool,
  logOut: func,
  authorizedUserItems: array,
}

export default connect(mapStateToProps, mapDispatchToProps)(MobileMenu)
